import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { FiEdit, FiPrinter, FiDownload } from "react-icons/fi";
import Button from "../../components/Button/Button";
import { getPurchaseOrders } from "../../api";
import { printPurchaseOrder, downloadPurchaseOrder } from "./pdfActions";
import "./_styles.purchase-orders.scss";

// Status labels as stored on the PO record (0/1 are the editable states).
const STATUS_LABELS = {
  0: "Open",
  1: "Partial",
  2: "Fulfilled",
  3: "Cancelled",
};
const statusLabel = (s) => STATUS_LABELS[s] ?? `Status ${s}`;

const formatMoney = (value) => `R ${Number(value || 0).toFixed(2)}`;

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getPurchaseOrders();
        setOrders(data);
      } catch (err) {
        console.error("Failed to load purchase orders", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = orders.filter((po) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      String(po.id).includes(term) ||
      (po.number || "").toLowerCase().includes(term) ||
      (po.doc_no || "").toLowerCase().includes(term) ||
      (po.supplier || "").toLowerCase().includes(term) ||
      (po.site || "").toLowerCase().includes(term)
    );
  });

  const handlePrint = async (po) => {
    try {
      await printPurchaseOrder(po.id);
    } catch (err) {
      console.error("Failed to generate PDF", err);
      alert("Could not generate the PDF.");
    }
  };

  const handleDownload = async (po) => {
    try {
      await downloadPurchaseOrder(po.id, po.number);
    } catch (err) {
      console.error("Failed to download PDF", err);
      alert("Could not download the PDF.");
    }
  };

  return (
    <div className="purchase-orders">
      <div className="po-header">
        <h3>Purchase Orders</h3>
        <Button
          title="Create New"
          onClick={() => navigate("/purchaseorders/new")}
        />
      </div>

      <div className="po-search">
        <FaMagnifyingGlass />
        <input
          type="text"
          placeholder="Search by PO #, vendor document no, supplier or site"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="po-table">
        <table>
          <thead>
            <tr>
              <th>PO #</th>
              <th>Vendor Document No</th>
              <th>Date</th>
              <th>Expected Delivery</th>
              <th>Supplier</th>
              <th>Site</th>
              <th>Status</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="empty">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty">
                  No purchase orders yet.
                </td>
              </tr>
            ) : (
              filtered.map((po) => (
                <tr
                  key={po.id}
                  onClick={() => navigate(`/purchaseorders/${po.id}`)}
                >
                  <td>{po.number || po.id}</td>
                  <td>{po.doc_no || "-"}</td>
                  <td>{po.date}</td>
                  <td>{po.expected_date || "-"}</td>
                  <td>{po.supplier || "-"}</td>
                  <td>{po.site || "-"}</td>
                  <td>
                    <span className={`status status-${po.status}`}>
                      {statusLabel(po.status)}
                    </span>
                  </td>
                  <td>{formatMoney(po.total)}</td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      title="Edit"
                      onClick={() => navigate(`/purchaseorders/${po.id}`)}
                    >
                      <FiEdit />
                    </button>
                    <button title="Print" onClick={() => handlePrint(po)}>
                      <FiPrinter />
                    </button>
                    <button title="Download" onClick={() => handleDownload(po)}>
                      <FiDownload />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseOrders;
